import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DashboardDialogFramePart, DialogAction, DialogContext } from '@eforall/dashboard';
import { SpecialistAvailabilityResult } from '@interfaces';
import { FuncService } from '../../../../../../services';
import { PersonFinderWidget } from '../../../../../../ui/widget/search';

export interface DirectlyAssignSpecialistDialogData {
	accId: number,
	topicId: number,
	topicName: string,
	languageId: number,
	language: string,
}

@Component({
	selector: 'directly-assign-specialist-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		PersonFinderWidget,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './directly-assign-specialist.dialog.html'
})

export class DirectlyAssignSpecialistDialog {

	public dialogRef = inject(MatDialogRef<DirectlyAssignSpecialistDialog>);
	public dialogContext: DialogContext<DirectlyAssignSpecialistDialogData> = inject(MAT_DIALOG_DATA);

	private func = inject(FuncService);

	public selectedPersonId: number | undefined = undefined;
	public specialistAvailability = signal<SpecialistAvailabilityResult | undefined>(undefined);

	public actions = computed<DialogAction[]>(() => {
		const actions: DialogAction[] = [];

		if (!this.dialogContext.data) return actions;

		const specialistAvailability = this.specialistAvailability();

		const canAdd = specialistAvailability && specialistAvailability?.hasVolunteerRecord && !specialistAvailability?.approvalIssues;

		if (!canAdd) {
			actions.push({
				id: 'check',
				cssClass: signal(undefined),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Check'),
				linkType: 'callback',
				callback: async () => await this.checkSpecialistAvailability(),
				willCloseDialog: false,
			});
		} else {
			actions.push({
				id: 'assign',
				cssClass: signal(undefined),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Assign'),
				linkType: 'callback',
				callback: async () => await this.addSpecialist(),
				willCloseDialog: true,
			});
		}

		return [
			{
				id: 'close',
				cssClass: signal(undefined),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Close'),
				linkType: "no-action",
				willCloseDialog: true,
			},
			...actions,
		]
	});

	setSelectedPersonId(personId: number) {
		this.selectedPersonId = personId;
		this.specialistAvailability.set(undefined);
	}

	async checkSpecialistAvailability() {
		const dialogData = this.dialogContext.data;
		if (!this.selectedPersonId || !dialogData) return;

		const data = await this.func.volunteering.specialistSignUp.checkSpecialistAvailability({ personId: this.selectedPersonId, topicId: dialogData.topicId, languageId: dialogData.languageId });
		this.specialistAvailability.set(data);
	}

	async addSpecialist() {
		const data = this.dialogContext.data;
		const availabilityResult = this.specialistAvailability();
		if (!this.selectedPersonId || !data || !availabilityResult || !availabilityResult.hasVolunteerRecord || availabilityResult.approvalIssues) return;

		await this.func.volunteering.specialistSignUp.directlyAssignSpecialistToTopic({
			personId: this.selectedPersonId,
			topicId: data.topicId,
			accId: data.accId
		});
	}

}